<template>
  <div class="recommendation-preview">
    <div class="recommendation-preview__engine-body-container">
      <div :id="'recommendation-engine-' + index">
        <div id="e-app"></div>
      </div>

      <div class="recommendation-preview__engine-body">
        <div v-show="isPrismaLoaded" class="engine-body__preview">
          <div
            :id="'engine-preview-' + index"
            class="disable-interaction"
          ></div>
          <div :id="'engine-inputs-' + index" class="disable-interaction"></div>
        </div>

        <div
          v-show="isPrismaLoaded"
          class="recommendation-preview__additional-infos"
        >
          <recommendation-color-option
            :product-color-options="recommendation.color_options"
            :recommended-material="recommendation.recommended_material"
            @color-material-changed="setMaterial"
            @color-case-device-changed="setCaseDeviceId"
            @color-option-changed="addedToCart = false"
          />

          <recommendation-name
            :name="recommendation.name"
            :color-options-length="recommendation.color_options.length"
          />

          <recommendation-price
            :old-price="recommendation.old_price"
            :price="recommendation.price"
          />
        </div>

        <div
          v-show="isPrismaLoaded"
          class="recommendation-preview__engine-export"
        >
          <div :id="'engine-export-' + index"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Ga4Parser from '~/utils/Ga4Parser'
import RecommendationName from '~/components/product-recommendation/recommendation-item/RecommendationName.vue'
import RecommendationPrice from '~/components/product-recommendation/recommendation-item/RecommendationPrice.vue'
import RecommendationColorOption from '~/components/product-recommendation/recommendation-item/RecommendationColorOption.vue'
import pushDataLayer from '~/mixins/gtm/push-data-layer'
import loadScript from '~/utils/loadScript'

const RECOMMENDATION_SCRIPT_URL =
  'https://unpkg.com/@gocasebr/prisma@recommendations'

export default {
  name: 'RecommendationPreview',
  components: {
    RecommendationName,
    RecommendationPrice,
    RecommendationColorOption,
  },
  mixins: [pushDataLayer],
  props: {
    recommendation: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      index: this.generateRandomIndex(),
      selectedMaterial: this.recommendation.recommended_material,
      selectedCaseDeviceId: null,
      isPrismaLoaded: false,
      addedToCart: false,
    }
  },
  computed: {
    baseCustomizationUrl() {
      return this.$store.getters[
        'productRecommendationsStore/baseCustomizationUrl'
      ]
    },
    exportComponent() {
      return document.querySelector(
        `#engine-export-${this.index} .export-component`
      )
    },
  },
  watch: {
    addedToCart() {
      this.updateExportComponentClass()
    },
  },
  created() {
    if (!this.bootstrapRecommendationEngineisAFunction()) {
      this.loadEngineScript(RECOMMENDATION_SCRIPT_URL).then(this.initEngine)
    } else {
      this.initEngine()
    }
  },
  beforeDestroy() {
    this.removeListeners()
  },
  methods: {
    bootstrapRecommendationEngineisAFunction() {
      return typeof window.bootstrapRecommendationEngine === 'function'
    },
    loadEngineScript(src) {
      return loadScript(src, {
        once: false,
        attrs: { id: 'ENGINE_RECOMMENDATIONS' },
      })
    },
    initEngine() {
      if (this.bootstrapRecommendationEngineisAFunction()) {
        this.addListeners()
        window.bootstrapRecommendationEngine(this.engineParams())
      }
    },
    engineParams() {
      return {
        nameOfEventExportImg: `exportImage-${this.index}`,
        useQrCodeUpload: false,
        appMountId: 'e-app',
        modalEngineId: `recommendation-engine-${this.index}`,
        onlyPreviewMode: true,
        liteMode: {
          previewMountId: `engine-preview-${this.index}`,
          previewHeight: 220,
          inputsMountId: `engine-inputs-${this.index}`,
          exportMountId: `engine-export-${this.index}`,
        },
        viewData: {
          btnExportName: this.$t('product_recommendations.add_to_cart'),
          btnExportClass: 'btn btn--small btn--block btn--orange',
          hasLnrCss: false,
          icons: false,
        },
        productData: {
          material: this.selectedMaterial,
          engineIdentifier: this.recommendation.engine_identifier,
        },
        product: {
          material: this.selectedMaterial,
          store: 'br',
          identifier: this.recommendation.engine_identifier,
        },
        recomendationMode: {
          product: {
            material: this.selectedMaterial,
            identifier: this.recommendation.engine_identifier,
          },
          base_customization_url: this.baseCustomizationUrl,
        },
        events: {
          exportImage: `exportImage-${this.index}`,
          loadComponents: `loadComponents-${this.index}`,
        },
      }
    },
    setMaterial(material) {
      this.selectedMaterial = material

      if (this.bootstrapRecommendationEngineisAFunction()) this.reloadPrisma()
    },
    setCaseDeviceId(caseDeviceId) {
      this.selectedCaseDeviceId = caseDeviceId
    },
    reloadPrisma() {
      window.bootstrapRecommendationEngine = null
      this.index = this.generateRandomIndex()
      this.removeListeners()
      this.unsetPrismaLoaded()

      this.loadEngineScript(RECOMMENDATION_SCRIPT_URL).then(this.initEngine)
    },
    generateRandomIndex() {
      return Math.floor(Math.random() * 99999)
    },
    addListeners() {
      window.addEventListener(`exportImage-${this.index}`, this.handleCallback)
      window.addEventListener(
        `loadComponents-${this.index}`,
        this.setPrismaLoaded
      )
    },
    removeListeners() {
      window.removeEventListener(
        `exportImage-${this.index}`,
        this.handleCallback
      )
      window.removeEventListener(
        `loadComponents-${this.index}`,
        this.setPrismaLoaded
      )
    },
    handleCallback(event) {
      this.addToCart(event.detail.img)
    },
    setPrismaLoaded(_event) {
      this.isPrismaLoaded = true
      this.$emit('prisma-loaded', this.isPrismaLoaded)
    },
    unsetPrismaLoaded() {
      this.isPrismaLoaded = false
      this.$emit('prisma-loaded', this.isPrismaLoaded)
    },
    async addToCart(imageUrl) {
      try {
        this.$displayOverlaidLoading()

        const response = await this.$axios.$post('/carts/populate', {
          variant_id: this.recommendation.variant_id,
          quantity: 1,
          customizations: {
            [this.recommendation.customization_id]: imageUrl,
          },
          number: this.$store.state.orderNumber,
          image_url: imageUrl,
          engine_information: {
            img: imageUrl,
          },
          case_device_id: this.selectedCaseDeviceId,
          added_by_suggestion: true,
          ...this.$store.getters.storeInfo,
        })

        if (response.order_quantity)
          this.$store.commit('SET_CART_QTY', response.order_quantity)

        this.$displayOverlaidLoading('close')
        this.pushToDataLayer()
        this.addedToCart = true
      } catch (err) {
        this.$displayOverlaidLoading('close')

        this.$displayTopMessage(this.$t('action_error'), {
          type: 'error',
        })
      }
    },
    pushToDataLayer() {
      const prod = {
        ...JSON.parse(JSON.stringify(this.recommendation)),
        price: this.recommendation.price,
        promotion: this.recommendation.promotion,
        caseTypeSelected: this.selectedCaseDeviceId,
      }

      const ga4Items = new Ga4Parser(this.$store.state).addToCart(prod)

      /* 
        Prevent Side Effect since ga4EventValue is an array, 
        gtm automaticly append a new item instead of create a new array  
      */
      this.pushDataLayer({
        ga4EventValue: undefined,
      })

      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'interactWithPage',
        eventAction: 'clickOnButton',
        eventLabel: 'addToCartFromCrossell',
        eventValue: {
          currency: this.$store.state.currency,
          product: {
            id: this.recommendation.id,
            name: this.recommendation.name,
            price: this.recommendation.price,
            sku: this.recommendation.sku,
            has_customization: true,
            variant_id: this.recommendation.variant_id,
            raw_name: this.recommendation.name,
          },
        },
        ga4EventValue: ga4Items,
      })
    },
    updateExportComponentClass() {
      const exportComponent = document.querySelector(
        `#engine-export-${this.index} .export-component`
      )
      if (exportComponent) {
        if (this.addedToCart) {
          exportComponent.classList.add('added-to-cart')
          exportComponent.textContent = this.$t(
            'product_recommendations.added_to_cart'
          )
        }
      }
    },
  },
}
</script>

<style lang="scss">
.disable-interaction {
  pointer-events: none;
}

.btn--small {
  font-size: 13px !important;
  padding: 10px 15px !important;
  height: auto !important;
  font-weight: 500 !important;
}

.btn--block {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  text-align: center !important;
}

.btn--orange {
  background: $v4_color_orange !important;
  color: white !important;

  &:hover {
    background: darken($v4_color_orange, 10%) !important;
  }
}

.added-to-cart {
  pointer-events: none;
  background-color: $v4_success_green !important;
  border-radius: 4px;
}

.recommendation__pricing--previous {
  text-decoration: line-through;
}

.recommendation__pricing--current {
  color: $v4_color_primary;
}

.recommendation-preview {
  width: 100%;
  padding: 10px;
}

.engine-body__preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.recommendation-preview__additional-infos {
  margin: 5px 0 5px 0;
}

.recommendation__name {
  color: $v4_color_text;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-x: hidden;
  max-width: 100%;
  font-size: inherit;
  font-weight: 400;
  margin: 5px 0 0;
}

.list-product__color {
  cursor: pointer;
}
</style>
